import { useState } from "react"
import { useNavigate } from "react-router-dom"
import AppShell from "@/components/AppShell/AppShell"
import AsciiArt from "@/components/AsciiArt/AsciiArt"
import AvatarStack from "@/components/AvatarStack/AvatarStack"
import Button from "@/components/Button/Button"
import CustomerModal from "@/components/CustomerModal/CustomerModal"
import type { CustomerField } from "@/components/CustomerModal/CustomerModal"
import DataTable from "@/components/DataTable/DataTable"
import type { Column } from "@/components/DataTable/DataTable"
import FilterPills from "@/components/FilterPills/FilterPills"
import RemoveDialog from "@/components/RemoveDialog/RemoveDialog"
import { ACTION_GLYPH, BinGlyph, BoxGlyph } from "@/components/RemoveDialog/removeGlyphs"
import StatusDot from "@/components/StatusDot/StatusDot"
import StatusScreen from "@/components/StatusScreen/StatusScreen"
import Tooltip from "@/components/Tooltip/Tooltip"
import ViewToggle from "@/components/ViewToggle/ViewToggle"
import type { ViewMode } from "@/components/ViewToggle/ViewToggle"
import {
  CUSTOMER_DIALOG_BLURB,
  CUSTOMER_DIALOG_TITLE,
  filedAside,
  removeActionLabel,
  roomsKeepRunning,
  sessionsGoingWith,
  sessionsStranded,
} from "@/content/archive"
import { PLANT_CYCLE } from "@/content/ascii"
import { UNTITLED_SESSION } from "@/content/customers"
import { matchesFilter } from "@/content/status"
import type { StateFilter } from "@/content/status"
import {
  TABLE_CUSTOMER,
  TABLE_DATE,
  TABLE_PARTICIPANTS,
  TABLE_SESSION,
  TABLE_STATUS,
  TABLE_VIEW,
} from "@/content/nav"
import { useFirestoreSubmit } from "@/hooks/useFirestoreWrite"
import { useExpiryTick } from "@/hooks/useExpiryTick"
import { invalidateFacilitatorIndex } from "@/hooks/useFacilitatorIndex"
import { useSessionParticipants } from "@/hooks/useSessionParticipants"
import type { FacilitatorIndex } from "@/hooks/useFacilitatorIndex"
import { createCustomer, removeCustomer } from "@/services/customers"
import { useAuthStore, selectAuthUid } from "@/store/authStore"
import { customerDetailPath, sessionPath } from "@/utils/routePaths"
import { relativeTime, shortDate } from "@/utils/relativeTime"
import { sessionState } from "@/utils/sessionLifecycle"
import type { FamilyNumber } from "@/theme/colors"
import type { Customer, RemovalKind, Session } from "@/types"
import styles from "@/styles/pages/CustomerList.module.css"

const EMPTY_DRAFT: Record<CustomerField, string> = { name: "", sector: "", size: "", about: "" }
type Draft = typeof EMPTY_DRAFT

/*
 * The workspace: every customer this facilitator may see, and every session
 * under them.
 *
 * Two reads of the same two lists. The grid is customers — one tile each, with
 * the state of their most recent session on it. The list is SESSIONS, flattened
 * across every customer, because "what ran and when" is a different question
 * from "who do we work with" and it was never answerable from a grid of tiles.
 *
 * Both lists come from `AppShell`, which already loaded them for the nav. That is
 * the whole reason the shell owns them: this screen reading its own copy would
 * be the same two queries twice on every visit.
 */
export default function CustomerList() {
  return <AppShell>{(index) => <CustomerWorkspace index={index} />}</AppShell>
}

function CustomerWorkspace({ index }: { index: FacilitatorIndex }) {
  const navigate = useNavigate()
  // The screen is only reachable behind `FacilitatorOnly`, so there is always a
  // uid here — but every read is scoped BY it, so it is threaded through
  // explicitly rather than read inside the services. A list that silently fell
  // back to "everyone's customers" is the bug this replaced.
  const uid = useAuthStore(selectAuthUid)
  const [view, setView] = useState<ViewMode>("grid")
  const [filter, setFilter] = useState<StateFilter>("all")
  const [draft, setDraft] = useState<Draft | null>(null)
  /* Which customer the dialog is about, and which way it is going. One variable:
     the two controls open the same dialog with different copy and a different
     bar to clear — see `RemoveDialog`. */
  const [removing, setRemoving] = useState<{ customer: Customer; kind: RemovalKind } | null>(null)
  const { submit, pending, error } = useFirestoreSubmit<string>()
  const removal = useFirestoreSubmit<boolean>()

  const { customers, sessions, archivedSessions, error: loadError, loading, reload } = index
  // One-shot reads, so nothing here re-renders on its own. The tick is what
  // makes a tile stop saying Live the moment its session's deadline passes.
  const now = useExpiryTick((sessions ?? []).map((session) => session.expiresAt))

  if (loadError !== null && customers === null) {
    return (
      <StatusScreen
        nested
        eyebrow="Customers"
        title="The customer list did not load."
        raw={loadError}
      >
        <Button loading={loading} onClick={reload}>
          Try again
        </Button>
      </StatusScreen>
    )
  }

  if (customers === null || sessions === null) {
    return <StatusScreen nested eyebrow="Customers" title="Loading customers." waiting />
  }

  const nameGiven = (draft?.name ?? "").trim().length > 0

  const create = async () => {
    if (!draft || !nameGiven || !uid) return
    // The creator becomes the customer's first facilitator in the same write:
    // a customer with no members is one nobody, including its author, can read
    // back — see `createCustomer`.
    const id = await submit(() =>
      createCustomer(uid, {
        name: draft.name.trim(),
        sector: draft.sector.trim(),
        size: draft.size.trim(),
        about: draft.about.trim(),
      })
    )
    // Null means the write failed; the dialog keeps what was typed and says why.
    if (id) {
      invalidateFacilitatorIndex()
      navigate(customerDetailPath(id))
    }
  }

  const applyRemoval = async () => {
    if (!removing || !uid) return
    const { customer, kind } = removing
    const ok = await removal.submit(async () => {
      await removeCustomer(customer.id, uid, kind)
      return true
    })
    if (ok) {
      setRemoving(null)
      invalidateFacilitatorIndex()
      index.reload()
    }
  }

  const field = (key: keyof Draft) => ({
    value: draft?.[key] ?? "",
    onChange: (event: { target: { value: string } }) =>
      setDraft((current) => ({ ...(current ?? EMPTY_DRAFT), [key]: event.target.value })),
  })

  /* Sessions arrive newest first, so the first one seen per customer is the most
     recent. The state of that one is what the customer tile reports. */
  const latest = new Map<string, Session>()
  const counts = new Map<string, number>()
  for (const session of sessions) {
    if (!latest.has(session.customerId)) latest.set(session.customerId, session)
    counts.set(session.customerId, (counts.get(session.customerId) ?? 0) + 1)
  }

  /* Their sessions that are ALREADY in the Archive, filed one at a time. They do
     not travel with the customer and do not come back with them, so they are
     counted apart from `counts` — see `sessionsGoingWith`. */
  const filedFor = (customerId: string) =>
    (archivedSessions ?? []).filter(
      (row) => !row.inherited && row.session.customerId === customerId
    ).length

  /* What the dialog warns about: archiving a customer does NOT stop a room that
     is running — the share link belongs to the session, and only `removeSession`
     ends it. Counted off the lists already in hand. */
  const liveRunsFor = (customerId: string) =>
    sessions.filter((session) => {
      if (session.customerId !== customerId) return false
      const state = sessionState(session, now)
      return state === "live" || state === "paused"
    }).length

  const shownCustomers = customers.filter((customer) => {
    const recent = latest.get(customer.id)
    // A customer with no sessions yet is a draft in every sense — nothing has
    // run — so it belongs under the Draft pill rather than nowhere.
    return matchesFilter(recent ? sessionState(recent, now) : "draft", filter)
  })
  const shownSessions = sessions.filter((session) =>
    matchesFilter(sessionState(session, now), filter)
  )

  /* The customer behind a session row, so the list view can offer the same two
     controls the grid tile does. By id rather than by the row's `customerName`,
     which is a denormalised copy and not a key. */
  const customerById = new Map(customers.map((customer) => [customer.id, customer]))

  const columns: readonly Column<Session>[] = [
    {
      key: "customer",
      header: TABLE_CUSTOMER,
      width: "28%",
      render: (session) => {
        /* The row is a session; these two controls are not. They act on the
           CUSTOMER, so they live in the customer's own cell rather than in the
           row's action column beside View — a bin at the end of a session row
           would read as "delete this session", which is a different act with a
           different dialog. Labelled with the customer's name for the same
           reason: the same customer owns several rows, and each one of these is
           the same press. */
        const customer = customerById.get(session.customerId)
        const name = customer?.name || session.customerName || "Untitled customer"
        return (
          <span className={styles.cellCustomer}>
            <span
              className={styles.cellDot}
              style={{ background: `var(--family-${familyFor(session.customerId)}-bloom)` }}
            />
            <span className={styles.cellCustomerName}>{name}</span>
            {/* Absent when the customer document did not come back with the
                list: without it the dialog cannot count their sessions or name
                what it is about to file away. */}
            {customer ? (
              <span className={styles.cellTools}>
                <Tooltip label={removeActionLabel("archived", name)} placement="top">
                  <button
                    type="button"
                    className={styles.cellTool}
                    aria-label={removeActionLabel("archived", name)}
                    onClick={() => setRemoving({ customer, kind: "archived" })}
                  >
                    <BoxGlyph size={ACTION_GLYPH} />
                  </button>
                </Tooltip>
                <Tooltip label={removeActionLabel("deleted", name)} placement="top">
                  <button
                    type="button"
                    className={`${styles.cellTool} ${styles.cellToolDelete}`}
                    aria-label={removeActionLabel("deleted", name)}
                    onClick={() => setRemoving({ customer, kind: "deleted" })}
                  >
                    <BinGlyph size={ACTION_GLYPH} />
                  </button>
                </Tooltip>
              </span>
            ) : null}
          </span>
        )
      },
    },
    {
      key: "session",
      header: TABLE_SESSION,
      width: "16%",
      render: (session) => session.name || UNTITLED_SESSION,
    },
    {
      key: "date",
      header: TABLE_DATE,
      width: "14%",
      render: (session) => shortDate(session.createdAt),
    },
    {
      key: "status",
      header: TABLE_STATUS,
      width: "14%",
      render: (session) => <StatusDot state={sessionState(session, now)} compact />,
    },
    {
      key: "participants",
      header: TABLE_PARTICIPANTS,
      width: "18%",
      render: (session) => <ParticipantsCell sessionId={session.id} />,
    },
    {
      key: "action",
      header: "",
      align: "right",
      render: (session) => (
        <button
          type="button"
          className={styles.cellAction}
          onClick={() => navigate(sessionPath(session.id))}
        >
          {TABLE_VIEW}
        </button>
      ),
    },
  ]

  return (
    <>
      <header className={styles.head}>
        <div className={styles.headText}>
          <h1 className={styles.title}>Customers</h1>
          <p className={styles.count}>
            {customers.length} {customers.length === 1 ? "customer" : "customers"} ·{" "}
            {sessions.length} {sessions.length === 1 ? "session" : "sessions"} run
          </p>
        </div>
        <Button onClick={() => setDraft(EMPTY_DRAFT)}>New Customer</Button>
      </header>

      <div className={styles.controls}>
        <ViewToggle value={view} onChange={setView} />
        <FilterPills value={filter} onChange={setFilter} />
      </div>

      {loadError !== null ? <p className={styles.error}>{loadError}</p> : null}

      {customers.length === 0 ? (
        <div className={styles.empty}>
          <div className={styles.emptyTitle}>No customers yet.</div>
          <div className={styles.emptyBody}>
            You see the customers you created and the ones shared with you. Add the first one, then
            start a session.
          </div>
        </div>
      ) : view === "grid" ? (
        <ul className={styles.grid}>
          {shownCustomers.map((customer, position) => (
            <li key={customer.id}>
              <CustomerCard
                customer={customer}
                position={position}
                recent={latest.get(customer.id) ?? null}
                now={now}
                runs={counts.get(customer.id) ?? 0}
                filed={filedFor(customer.id)}
                onOpen={() => navigate(customerDetailPath(customer.id))}
                onRemove={(kind) => setRemoving({ customer, kind })}
              />
            </li>
          ))}
        </ul>
      ) : (
        <DataTable
          caption="Every session, newest first"
          columns={columns}
          rows={shownSessions}
          rowKey={(session) => session.id}
          empty={<div className={styles.empty}>Nothing matches that filter.</div>}
        />
      )}

      {removing !== null ? (
        <RemoveDialog
          kind={removing.kind}
          title={CUSTOMER_DIALOG_TITLE[removing.kind]}
          description={CUSTOMER_DIALOG_BLURB[removing.kind]}
          detail={
            <>
              {sessionsGoingWith(
                counts.get(removing.customer.id) ?? 0,
                removing.kind,
                filedFor(removing.customer.id)
              )}
              {liveRunsFor(removing.customer.id) > 0
                ? ` ${roomsKeepRunning(liveRunsFor(removing.customer.id), removing.kind)}`
                : ""}
            </>
          }
          warning={
            filedFor(removing.customer.id) > 0
              ? sessionsStranded(
                  filedFor(removing.customer.id),
                  removing.kind,
                  (counts.get(removing.customer.id) ?? 0) > 0
                )
              : undefined
          }
          /* Typed only for a delete: a customer is shared, so deleting takes it
             out of every member's lists, and there is no rule that could reserve
             the act for its creator. */
          confirmWith={
            removing.kind === "deleted" ? removing.customer.name || "Untitled customer" : undefined
          }
          error={removal.error}
          pending={removal.pending}
          onClose={() => {
            setRemoving(null)
            removal.clearError()
          }}
          onConfirm={() => void applyRemoval()}
        />
      ) : null}

      {draft !== null ? (
        <CustomerModal
          mode="create"
          binding={field}
          error={error}
          onClose={() => setDraft(null)}
          actions={
            <>
              <Button variant="quiet" onClick={() => setDraft(null)}>
                Cancel
              </Button>
              <Button disabled={!nameGiven} loading={pending} onClick={create}>
                {pending ? "Creating…" : "Create Customer"}
              </Button>
            </>
          }
        />
      ) : null}
    </>
  )
}

/**
 * Who was in the room, per row.
 *
 * One roster read per SESSION — the rosters are not on the session document, so
 * there is no cheaper source, but `useSessionParticipants` holds each one for
 * the visit and every row asking for the same session gets the same read. That
 * is what makes the cost bearable here: the table only renders the sessions
 * that pass the filter, and moving the filter or toggling the view remounts
 * rows without re-reading them.
 */
function ParticipantsCell({ sessionId }: { sessionId: string }) {
  const participants = useSessionParticipants(sessionId)
  if (participants.length === 0) return <span className={styles.cellEmpty}>—</span>

  return (
    <span className={styles.cellPeople}>
      <AvatarStack
        people={participants.map((person, index) => ({
          id: person.id,
          name: person.name || "Guest",
          family: ((index % 7) + 1) as FamilyNumber,
        }))}
        size={28}
        max={5}
      />
      <span className={styles.cellPeopleCount}>{participants.length}</span>
    </span>
  )
}

interface CustomerCardProps {
  customer: Customer
  /** Position in the grid. Picks the motif and its accent — nothing is stored. */
  position: number
  recent: Session | null
  /** The workspace's tick, so a tile expires at the same instant its row does. */
  now: number
  runs: number
  /** Already archived or deleted, and therefore NOT in `runs` — see `filedAside`. */
  filed: number
  onOpen: () => void
  onRemove: (kind: RemovalKind) => void
}

function CustomerCard({
  customer,
  position,
  recent,
  now,
  runs,
  filed,
  onOpen,
  onRemove,
}: CustomerCardProps) {
  const motif = PLANT_CYCLE[position % PLANT_CYCLE.length]
  const name = customer.name || "Untitled customer"

  return (
    <div className={styles.card}>
      <button
        type="button"
        className={styles.cardOpen}
        aria-label={`Open ${name}`}
        onClick={onOpen}
      />

      <span className={styles.cardTop}>
        <StatusDot state={recent ? sessionState(recent, now) : "draft"} />
        <span className={styles.runs} title={filed > 0 ? filedAside(filed) : undefined}>
          {runs} {runs === 1 ? "session" : "sessions"}
          {filed > 0 ? ` · ${filed} filed` : ""}
        </span>
      </span>

      <span className={styles.name}>{name}</span>

      <span className={styles.motif}>
        <AsciiArt motif={motif} family={familyFor(customer.id)} />
      </span>

      <span className={styles.cardFoot}>
        <span className={styles.sector}>
          {[customer.sector, customer.size].filter(Boolean).join(" · ") || "No sector set"}
        </span>

        <span className={styles.cardTools}>
          <Tooltip label={removeActionLabel("archived", name)} placement="top">
            <button
              type="button"
              className={styles.cardTool}
              aria-label={removeActionLabel("archived", name)}
              onClick={() => onRemove("archived")}
            >
              <BoxGlyph size={ACTION_GLYPH} />
            </button>
          </Tooltip>
          <Tooltip label={removeActionLabel("deleted", name)} placement="top">
            <button
              type="button"
              className={`${styles.cardTool} ${styles.cardToolDelete}`}
              aria-label={removeActionLabel("deleted", name)}
              onClick={() => onRemove("deleted")}
            >
              <BinGlyph size={ACTION_GLYPH} />
            </button>
          </Tooltip>
        </span>

        <span className={styles.last}>
          {recent ? `Last run ${relativeTime(recent.updatedAt)}` : "Never run"}
        </span>
      </span>
    </div>
  )
}

/**
 * A stable family colour for a customer, from its id.
 *
 * Not stored, and not the position in the list: a customer that moves when a
 * newer one is created would otherwise change colour, and the colour is the only
 * thing making one tile findable in a grid of six.
 */
function familyFor(id: string): FamilyNumber {
  let sum = 0
  for (let i = 0; i < id.length; i++) sum = (sum + id.charCodeAt(i)) % 7
  return (sum + 1) as FamilyNumber
}
